import { Row, Col, Card, Table, Tag, Button, Modal, Input, Select } from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import apiPath from "../../constants/apiPath";
import moment from "moment";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { EyeIcon, ArrowLeftIcon } from "lucide-react";
import useDebounce from "../../hooks/useDebounce";
import { getSortOrder } from "../../constants/conts";
const Search = Input.Search;

function ActivityLog() {

  const sectionName   =   "Activity Logs";

  const { request }   =   useRequest();
  const params        =   useParams();
  const location      =   useLocation();
  const navigate      =   useNavigate();
  const query         =   new URLSearchParams(location.search);

  // ---- parse initial values from query string ----
  const initialPage = parseInt(query.get("page")) || 1;
  const initialPageSize = parseInt(query.get("pageSize")) || 20;
  const initialSort = query.get("sort") || "";
  const initialScreenName = query.get("screen_name") || "";

  // ---- state ----
  const [searchText, setSearchText] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: initialPage,
    pageSize: initialPageSize,
    total: 0
  });
  const [sortBy, setSortBy] = useState(initialSort);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [screenNames, setScreenNames] = useState([]);
  const [selectedScreenName, setSelectedScreenName] = useState(initialScreenName);

  const debouncedSearchText = useDebounce(searchText, 500);

  // ---- sync state -> query string ----
  useEffect(() => {
    const queryParams = new URLSearchParams();
    if (pagination.current > 1) queryParams.set("page", pagination.current);
    if (pagination.pageSize && pagination.pageSize !== 20) queryParams.set("pageSize", pagination.pageSize);
    if (debouncedSearchText) queryParams.set("search", debouncedSearchText);
    if (sortBy) queryParams.set("sort", sortBy);
    if (selectedScreenName) queryParams.set("screen_name", selectedScreenName);

    navigate({ search: queryParams.toString() }, { replace: true });
  }, [
    pagination.current,
    pagination.pageSize,
    debouncedSearchText,
    sortBy,
    selectedScreenName,
    navigate,
  ]);

  // ---- fetch screen names ----
  useEffect(() => {
    request({
      url: apiPath.activityScreenNames,
      method: "GET",
      onSuccess: (response) => {
        setScreenNames(response.data.screen_names || []);
      },
      onError: (error) => {
        console.error("Error fetching screen names:", error);
      },
    });
  }, []);

  // ---- fetch data ----
  useEffect(() => {
    fetchData(params.id, pagination, sortBy);
  }, [
    params.id,
    debouncedSearchText,
    sortBy,
    selectedScreenName,
    pagination.current,
    pagination.pageSize,
  ]);

  const fetchData = (id, pageInfo, sortValue = "") => {
    setLoading(true);
    let url = `${apiPath.activityLogs}/${id}?page=${pageInfo.current}&limit=${pageInfo.pageSize}&search=${debouncedSearchText}&sort=${sortValue || ""}`;
    if (selectedScreenName) {
      url += `&screen_name=${encodeURIComponent(selectedScreenName)}`;
    }
    request({
      url: url,
      method: "GET",
      onSuccess: (response) => {
        const data = response.data.list;
        setLogs(data.docs || []);
        setPagination({
          current: data.page,
          pageSize: data.limit,
          total: data.totalDocs || 0,
        });
        setLoading(false);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleTableChange = (pag, filters, sorter) => {
    const order = sorter?.order;
    const field = sorter?.field;

    let nextSort = "";
    if (order) {
      nextSort = `${field}:${order === "ascend" ? "asc" : "desc"}`;
    }

    setSortBy(nextSort);
    setPagination((prev) => ({
      ...prev,
      current: pag?.current || 1,
      pageSize: pag?.pageSize || prev.pageSize,
    }));
  };

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const onScreenNameChange = (value) => {
    setSelectedScreenName(value || "");
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const getActivityTypeBadge = (type) => {
    const typeMap = {
      PRODUCT_VIEW: { color: "pink" },
      PROFILE_VIEW: { color: "blue" },
      LOGIN: { color: "green" },
      LOGOUT: { color: "lime" },
      CREATE: { color: "cyan" },
      UPDATE: { color: "orange" },
      DELETE: { color: "red" },
      DOWNLOAD: { color: "purple" },
      API_REQUEST: { color: "magenta" },
      CLICK: { color: "gold" },
      API_RESPONSE: { color: "volcano" },
    };
    const config = typeMap[type] || { color: "default" };
    return (
      <Tag color={config.color}> {type?.replace(/_/g, ' ')} </Tag>
    );
  };

  const columns = [
    {
      title: "Requested By",
      key: "user",
      render: (_, record) => (
        <div>
          <div className="font-medium cap">{record.user_id?.name || "-"}</div>
          <div className="text-gray-500 text-sm">{record.user_id?.email || "-"}</div>
        </div>
      ),
    },
    {
      title: "Event Type",
      dataIndex: "event_type",
      key: "event_type",
      width: 200,
      sorter: true,
      sortOrder: getSortOrder(sortBy, "event_type"),
      render: (text, record) => (
        <div>
          {getActivityTypeBadge(text)}
          {record.screen_name && (
            <div className="text-gray-500 text-sm mt-1">{record.screen_name}</div>
          )}
        </div>
      ),
    },
    {
      title: "Segment",
      key: "segment",
      render: (_, record) => record.segment_id?.en_name || "-",
    },
    {
      title: "Method",
      key: "method",
      render: (_, record) => {
        const method = record.meta_data?.method || "-";
        return (
          <Tag
            color={
              method === "GET"
                ? "blue"
                : method === "POST"
                ? "green"
                : method === "PUT"
                ? "orange"
                : method === "DELETE"
                ? "red"
                : "default"
            }
          >
            {method}
          </Tag>
        );
      },
    },
    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "duration"),
      render: (val) => <span className="text-blue-600 font-medium">{val || "-"}</span>,
    },
    {
      title: "Requested Date & Time",
      dataIndex: "created_at",
      key: "created_at",
      sorter: true,
      sortOrder: getSortOrder(sortBy, "created_at"),
      render: (date) => moment(date).format("DD-MMM-YYYY HH:mm A"),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedRecord(record);
            setModalVisible(true);
          }}
        >
          <EyeIcon size={14} /> View
        </Button>
      ),
    },
  ];

  const renderDetailModal = () => {
    if (!selectedRecord) return null;

    return (
      <Modal
        title="Activity Log Details"
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedRecord(null);
        }}
        width={1000}
      >
        <Row gutter={[16, 16]}>
          {/* User Information */}
          <Col span={24} md={12}>
            <Card title="User Information" variant={false}>
              <div className="view-inner-cls">
                <h5>Name:</h5>
                <h6 className="cap">{selectedRecord.user_id?.name || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Email Address:</h5>
                <h6>{selectedRecord.user_id?.email || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Phone Number:</h5>
                <h6>{selectedRecord.user_id?.phone || "-"}</h6>
              </div>
            </Card>
          </Col>

          {/* Request Information */}
          <Col span={24} md={12}>
            <Card title="Request Information" variant={false}>
              <div className="view-inner-cls">
                <h5>Method:</h5>
                <h6>
                  <Tag
                    color={
                      selectedRecord.meta_data?.method === "GET"
                        ? "blue"
                        : selectedRecord.meta_data?.method === "POST"
                        ? "green"
                        : selectedRecord.meta_data?.method === "PUT"
                        ? "orange"
                        : "red"
                    }
                  >
                    {selectedRecord.meta_data?.method || "-"}
                  </Tag>
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>URL:</h5>
                <h6> {selectedRecord.meta_data?.url || "-"} </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Duration:</h5>
                <h6 className="text-blue-600 font-medium">
                  {selectedRecord.duration ? selectedRecord.duration : "-"}
                </h6>
              </div>
            </Card>
          </Col>

          {/* Activity Details */}
          <Col span={24} md={12}>
            <Card title="Activity Details" variant={false}>
              <div className="view-inner-cls">
                <h5>Event Type:</h5>
                <h6>{getActivityTypeBadge(selectedRecord.event_type)}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Screen Name:</h5>
                <h6>{selectedRecord.screen_name || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Segment:</h5>
                <h6>{selectedRecord.segment_id?.en_name || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Device Type:</h5>
                <h6>{selectedRecord.device_type || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>User Agent:</h5>
                <h6>{selectedRecord.user_agent || "-"}</h6>
              </div>
            </Card>
          </Col>

          {/* Timeline */}
          <Col span={24} md={12}>
            <Card title="Timeline" variant={false}>
              <div className="view-inner-cls">
                <h5>Start Time:</h5>
                <h6>{selectedRecord.start_time || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>End Time:</h5>
                <h6>{selectedRecord.end_time || "-"}</h6>
              </div>

              <div className="view-inner-cls">
                <h5>Created On:</h5>
                <h6>
                  {selectedRecord.created_at
                    ? moment(selectedRecord.created_at).format("DD-MMM-YYYY HH:mm:ss")
                    : "-"}
                </h6>
              </div>

              <div className="view-inner-cls">
                <h5>Updated On:</h5>
                <h6>
                  {selectedRecord.updated_at
                    ? moment(selectedRecord.updated_at).format("DD-MMM-YYYY HH:mm:ss")
                    : "-"}
                </h6>
              </div>
            </Card>
          </Col>

          {/* Meta Data */}
          <Col span={24}>
            <Card title="Meta Data" variant={false}>
              <div
                style={{
                  background: "#f5f5f5",
                  padding: "12px",
                  borderRadius: "4px",
                  maxHeight: "250px",
                  overflowY: "auto",
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: "monospace",
                    fontSize: "12px",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                  }}
                >
                  {selectedRecord.meta_data
                    ? typeof selectedRecord.meta_data === "string"
                      ? selectedRecord.meta_data
                      : JSON.stringify(selectedRecord.meta_data, null, 2)
                    : "-"}
                </pre>
              </div>
            </Card>
          </Col>
        </Row>
      </Modal>
    );
  };

  return (
    <div className="tabled customerMain">
      <Row gutter={[24, 0]}>
        <Col xs="24" xl={24}>
          <Card
            variant={false}
            className="criclebox tablespace mb-24"
            title={sectionName}
            extra={
              <>
                <Select
                  allowClear
                  placeholder="Filter by Screen"
                  value={selectedScreenName || undefined}
                  onChange={onScreenNameChange}
                  style={{ height: 40 }}
                  className="w-full"
                >
                  {screenNames.map((screen) => (
                    <Select.Option key={screen} value={screen}>
                      {screen}
                    </Select.Option>
                  ))}
                </Select>

                <div className="searchOuter">
                  <Search
                    className="mainSearch"
                    size="large"
                    allowClear
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                    placeholder="Search By Event Type, Screen Name"
                  />
                </div>

                <Link className="ant-btn ant-btn-secondary" to={`/user?${location.search}`}>
                  <ArrowLeftIcon size={15} /> Back
                </Link>
              </>
            }
          >
            <div className="table-responsive customPagination">
              <Table
                loading={loading}
                columns={columns}
                dataSource={logs}
                rowKey="_id"
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ["10", "20", "30", "50", "100"],
                }}
                onChange={handleTableChange}
                className="ant-border-space"
              />
            </div>
          </Card>
        </Col>

        {renderDetailModal()}
      </Row>
    </div>
  );
}

export default ActivityLog;
